/**
 * Die Seite „Deine Daten“ — Export, Wo liegt was, Löschen. (17.10.2026, Plan „Deine Daten“, Bahn B)
 *
 * Gruppe „Du“, nach „Persönlichkeit“. Drei Karten:
 *   1. „Deine Daten mitnehmen“: Teile wählen, Export starten, Fortschritt, Download, Export löschen.
 *   2. „Wo liegt was“: Zahlen je Bereich, Speicherort, Sicherungsfrist, Link zur Datenschutzerklärung.
 *   3. „Löschen“: Verlauf kürzen (ehrlich: blendet nur aus), Papierkorb (bis Bahn E: nichts), Konto löschen anfragen.
 *
 * Die Seite kennt den Server nur über `DatenApi` (daten-api.ts) und den Vertrag shared/daten-export.ts — Tests reichen Attrappen.
 * ⚠️ Der Abfrage-Takt (5 s) läuft nur, solange ein Export wartet/läuft UND die Seite im Dokument steht; `destroy()` stoppt ihn.
 * ⚠️ iPhone-App: `<a download>` lädt in der WebView nicht zuverlässig. Dort geht der Tipp an den externen Öffner (Safari).
 */
import type { DatenUebersicht, ExportStand, ExportTeil } from "../../../shared/daten-export.js";
import { EXPORT_VERFALL_H } from "../../../shared/daten-export.js";
import type { DatenApi } from "./daten-api.js";
import { einstAbschnitt, einstSchalter, einstText } from "./einst-bausteine.js";

export interface DeineDatenKontext {
  uid: number | null | undefined;
  api: DatenApi;
  /** Läuft die App nativ (iPhone)? Dann öffnet der Download außerhalb der WebView. */
  nativ?: boolean;
  /** Öffnet eine Adresse außerhalb der App; Vorgabe: `window.open(url, "_system")`. */
  oeffneExtern?: (url: string) => void;
  /** Kürzt den Verlauf (der bestehende Weg der Verlauf-Karte). */
  verlaufKuerzen?: () => void | Promise<void>;
  /** Takt der Abfrage in ms (Vorgabe 5000). */
  taktMs?: number;
  /** Die Uhr (für Tests ersetzbar). */
  jetzt?: () => number;
}

export interface DeineDatenSeite {
  elemente: HTMLElement[];
  destroy(): void;
}

/** Bytes in Worten: „12 MB“, „3,5 GB“. Rein. */
export function bytesText(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  const stufen: Array<[number, string]> = [
    [1024 ** 3, "GB"],
    [1024 ** 2, "MB"],
    [1024, "KB"],
  ];
  for (const [g, e] of stufen) {
    if (bytes >= g) {
      const w = bytes / g;
      return `${w >= 10 || e === "KB" ? Math.round(w) : Math.round(w * 10) / 10} ${e}`.replace(".", ",");
    }
  }
  return "1 KB";
}

/** Tausenderpunkt, unabhängig von der Sprachausstattung des Geräts. */
const zahl = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

/** Datum und Uhrzeit für die Statuszeile („17.10.2026, 14:05“). Rein. */
export function zeitText(iso: string | null): string {
  const t = iso ? new Date(iso) : null;
  if (!t || Number.isNaN(t.getTime())) return "";
  return t.toLocaleString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Der Satz der Statuszeile zu einem Stand. Rein. */
export function standText(s: ExportStand | null, jetzt: number): string {
  if (!s || s.status === "keiner") return "Noch kein Export erstellt.";
  if (s.status === "wartet") return "Wartet, bis ein anderer Export fertig ist …";
  if (s.status === "laeuft") {
    const f = s.fortschritt;
    return f && f.gesamt > 0 ? `Wird vorbereitet … ${zahl(f.erledigt)} von ${zahl(f.gesamt)}` : "Wird vorbereitet …";
  }
  if (s.status === "fehler") return `Der Export ist fehlgeschlagen${s.fehler ? `: ${s.fehler}` : ""}. Du kannst es noch einmal versuchen.`;
  if (abgelaufen(s, jetzt)) return "Der Export ist abgelaufen. Erstelle einen neuen.";
  const bis = zeitText(s.verfaelltAm);
  return bis ? `Fertig — zum Herunterladen bis ${bis}.` : `Fertig — ${EXPORT_VERFALL_H} Stunden zum Herunterladen.`;
}

function abgelaufen(s: ExportStand, jetzt: number): boolean {
  if (!s.verfaelltAm) return false;
  const t = new Date(s.verfaelltAm).getTime();
  return Number.isFinite(t) && t <= jetzt;
}

const belegt = (s: ExportStand | null): boolean => !!s && (s.status === "wartet" || s.status === "laeuft");

function knopf(text: string, art: "primary" | "ghost" | "danger"): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = `btn btn--${art}`;
  b.textContent = text;
  return b;
}

function satz(text: string, klasse = "einst-feld-satz"): HTMLElement {
  const p = document.createElement("p");
  p.className = klasse;
  p.textContent = text;
  return p;
}

/** Eine Statuszeile, die Hilfsmittel vorlesen (`role="status"`). */
function meldung(klasse: string): HTMLElement {
  const m = document.createElement("p");
  m.className = klasse;
  m.setAttribute("role", "status");
  m.setAttribute("aria-live", "polite");
  return m;
}

/** Die Seite. */
export function baueDeineDatenSeite(k: DeineDatenKontext): DeineDatenSeite {
  if (k.uid === null || k.uid === undefined) {
    return { elemente: [satz("Bitte melde dich an, um deine Daten zu sehen.", "einst-seite-satz")], destroy: () => undefined };
  }
  const jetzt = k.jetzt ?? Date.now;
  const takt = k.taktMs ?? 5000;
  let zerstoert = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let aktuell: ExportStand | null = null;
  let uebersicht: DatenUebersicht | null = null;
  const gewaehlt = new Set<ExportTeil>(["texte"]);

  /* ───── Karte 1: mitnehmen ───── */
  const mit = einstAbschnitt("Deine Daten mitnehmen");
  mit.abschnitt.classList.add("einst-daten-mitnehmen");
  const teileKopf = document.createElement("div");
  teileKopf.className = "einst-feld";
  teileKopf.append(einstText("Texte", "Gedächtnis, Chats, Projekte, Kapitel, Profil — immer dabei."));

  const zahlenSatz = (kennung: string, leer: string): string => {
    const b = uebersicht?.bereiche.find((x) => x.kennung === kennung);
    return b ? `${zahl(b.anzahl)} Dateien, ${bytesText(b.bytes)}` : leer;
  };
  const mediathek = einstSchalter("Mediathek", "", false, (an) => wahl("mediathek", an));
  const aufnahmen = einstSchalter("Aufnahmen", "", false, (an) => wahl("aufnahmen", an));
  const mediathekSatz = mediathek.zeile.querySelector<HTMLElement>(".einst-feld-satz")!;
  const aufnahmenSatz = aufnahmen.zeile.querySelector<HTMLElement>(".einst-feld-satz")!;
  const zeigeZahlen = (): void => {
    mediathekSatz.textContent = `Hochgeladene Bilder und Dateien. ${zahlenSatz("mediathek", "")}`.trim();
    aufnahmenSatz.textContent = `Deine Tonaufnahmen, sie sind groß. ${zahlenSatz("aufnahmen", "")}`.trim();
  };
  function wahl(teil: ExportTeil, an: boolean): void {
    if (an) gewaehlt.add(teil);
    else gewaehlt.delete(teil);
  }
  zeigeZahlen();

  const aktion = document.createElement("div");
  aktion.className = "einst-feld einst-feld--gestapelt";
  const start = knopf("Export erstellen", "primary");
  start.classList.add("einst-daten-start");
  const stand = meldung("einst-feld-satz einst-daten-stand");
  const fehler = meldung("einst-feld-satz einst-daten-fehler");
  fehler.hidden = true;
  const dateien = document.createElement("ul");
  dateien.className = "einst-daten-dateien";
  dateien.setAttribute("aria-label", "Fertige Dateien");
  const entfernen = knopf("Export löschen", "ghost");
  entfernen.hidden = true;
  const nativHinweis = satz("Auf dem iPhone öffnet sich Safari zum Herunterladen.", "einst-feld-satz einst-daten-hinweis");
  nativHinweis.hidden = !k.nativ;
  aktion.append(start, stand, fehler, dateien, entfernen, nativHinweis);
  mit.karte.append(teileKopf, mediathek.zeile, aufnahmen.zeile, aktion);

  const meldeFehler = (e: unknown): void => {
    fehler.textContent = e instanceof Error && e.message ? e.message : "Das hat nicht geklappt. Bitte später noch einmal.";
    fehler.hidden = false;
  };

  const oeffne = k.oeffneExtern ?? ((url: string): void => void window.open(url, "_system"));
  const dateiLink = (name: string, bytes: number, url: string): HTMLElement => {
    const li = document.createElement("li");
    const a = document.createElement("a");
    a.className = "btn btn--ghost einst-daten-datei";
    a.href = url;
    a.setAttribute("download", name);
    a.textContent = `${name} (${bytesText(bytes)})`;
    if (k.nativ) {
      a.addEventListener("click", (ev) => {
        ev.preventDefault();
        oeffne(new URL(url, window.location.href).href);
      });
    }
    li.appendChild(a);
    return li;
  };

  const zeige = (s: ExportStand | null): void => {
    aktuell = s;
    stand.textContent = standText(s, jetzt());
    const laeuft = belegt(s);
    start.disabled = laeuft;
    start.setAttribute("aria-busy", String(laeuft));
    const brauchbar = !!s && s.status === "fertig" && !abgelaufen(s, jetzt());
    dateien.replaceChildren();
    if (brauchbar) for (const t of s.teile) for (const d of t.dateien) dateien.appendChild(dateiLink(d.name, d.bytes, d.url));
    dateien.hidden = dateien.childElementCount === 0;
    entfernen.hidden = !(s && s.id && (s.status === "fertig" || s.status === "fehler"));
  };

  const planeAbfrage = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (zerstoert || !belegt(aktuell)) return;
    timer = setTimeout(() => {
      timer = null;
      // Die Seite steht nicht mehr im Dokument (andere Seite geöffnet): der Takt endet.
      if (zerstoert || !mit.abschnitt.isConnected) return;
      void k.api
        .stand()
        .then((s) => zeige(s))
        .catch(() => undefined)
        .then(planeAbfrage);
    }, takt);
  };

  start.addEventListener("click", () => {
    if (belegt(aktuell)) return;
    fehler.hidden = true;
    start.disabled = true;
    const teile: ExportTeil[] = (["texte", "mediathek", "aufnahmen"] as const).filter((t) => gewaehlt.has(t));
    void k.api
      .starten(teile)
      .then((s) => {
        zeige(s);
        planeAbfrage();
      })
      .catch((e: unknown) => {
        meldeFehler(e);
        zeige(aktuell);
      });
  });

  entfernen.addEventListener("click", () => {
    const id = aktuell?.id;
    if (!id) return;
    fehler.hidden = true;
    entfernen.disabled = true;
    void k.api
      .loeschen(id)
      .then(() => zeige({ id: null, status: "keiner", teile: [], fortschritt: null, angelegtAm: null, verfaelltAm: null, fehler: null }))
      .catch(meldeFehler)
      .then(() => {
        entfernen.disabled = false;
      });
  });

  zeige(null);
  stand.textContent = "Stand wird abgefragt …";
  void k.api
    .stand()
    .then((s) => {
      if (zerstoert) return;
      zeige(s);
      planeAbfrage();
    })
    .catch(() => {
      if (!zerstoert) zeige(null);
    });

  /* ───── Karte 2: wo liegt was ───── */
  const wo = einstAbschnitt("Wo liegt was");
  wo.abschnitt.classList.add("einst-daten-wo");
  const tabelle = document.createElement("table");
  tabelle.className = "einst-daten-tabelle";
  const beschriftung = document.createElement("caption");
  beschriftung.className = "einst-daten-kopf";
  beschriftung.textContent = "Was Vespera von dir speichert";
  tabelle.appendChild(beschriftung);
  const tkopf = tabelle.createTHead().insertRow();
  for (const t of ["Bereich", "Anzahl", "Größe"]) {
    const th = document.createElement("th");
    th.scope = "col";
    th.textContent = t;
    tkopf.appendChild(th);
  }
  const tkoerper = tabelle.createTBody();
  const sicherung = satz("", "einst-feld-satz einst-daten-sicherung");
  const ladeSatz = meldung("einst-feld-satz einst-daten-stand");
  ladeSatz.textContent = "Wird gezählt …";
  const datenschutz = document.createElement("a");
  datenschutz.className = "btn btn--ghost einst-daten-link";
  datenschutz.href = "/datenschutz.html";
  datenschutz.target = "_blank";
  datenschutz.rel = "noopener";
  datenschutz.textContent = "Datenschutzerklärung";
  const woZeile = document.createElement("div");
  woZeile.className = "einst-feld einst-feld--gestapelt";
  woZeile.append(ladeSatz, tabelle, sicherung, datenschutz);
  tabelle.hidden = true;
  wo.karte.appendChild(woZeile);

  void k.api
    .uebersicht()
    .then((u) => {
      if (zerstoert) return;
      uebersicht = u;
      zeigeZahlen();
      for (const b of u.bereiche) {
        const r = tkoerper.insertRow();
        const name = r.insertCell();
        const titel = document.createElement("span");
        titel.className = "einst-feld-titel";
        titel.textContent = b.titel;
        const ort = document.createElement("span");
        ort.className = "einst-feld-satz";
        ort.textContent = b.ort;
        name.append(titel, ort);
        r.insertCell().textContent = zahl(b.anzahl);
        r.insertCell().textContent = bytesText(b.bytes);
      }
      tabelle.hidden = false;
      ladeSatz.hidden = true;
      sicherung.textContent = `Server in Europa, privat betrieben. Sicherungen laufen nach ${u.sicherungTage} Tagen ab. Stand: ${zeitText(u.stand)}.`;
    })
    .catch((e: unknown) => {
      if (zerstoert) return;
      ladeSatz.textContent = e instanceof Error && e.message ? e.message : "Die Übersicht ist gerade nicht abrufbar.";
    });

  /* ───── Karte 3: löschen ───── */
  const weg = einstAbschnitt("Löschen");
  weg.abschnitt.classList.add("einst-daten-weg");

  const kuerzen = document.createElement("div");
  kuerzen.className = "einst-feld";
  const kuerzenKnopf = knopf("Verlauf kürzen", "ghost");
  kuerzenKnopf.addEventListener("click", () => void k.verlaufKuerzen?.());
  kuerzen.append(einstText("Verlauf kürzen", "Blendet ältere Nachrichten aus. Sie bleiben im Archiv — gelöscht wird nichts."), kuerzenKnopf);

  const korb = document.createElement("div");
  korb.className = "einst-feld";
  korb.append(einstText("Papierkorb", "Gelöschtes ist sofort weg. Einen Papierkorb gibt es noch nicht."));

  const konto = document.createElement("div");
  konto.className = "einst-feld einst-feld--gestapelt einst-daten-gefahr";
  const haken = document.createElement("input");
  haken.type = "checkbox";
  haken.id = "einst-daten-haken";
  haken.className = "einst-daten-haken";
  const hakenText = document.createElement("label");
  hakenText.className = "einst-daten-haken-zeile";
  hakenText.htmlFor = haken.id;
  const hakenWort = document.createElement("span");
  hakenWort.className = "einst-feld-satz";
  hakenWort.textContent = "Ich weiß, dass dann alles weg ist.";
  hakenText.append(haken, hakenWort);
  const anfrage = knopf("Konto löschen …", "danger");
  anfrage.disabled = true;
  const bestaetigung = document.createElement("div");
  bestaetigung.className = "einst-daten-bestaetigung";
  bestaetigung.setAttribute("role", "alertdialog");
  bestaetigung.setAttribute("aria-label", "Konto löschen bestätigen");
  bestaetigung.hidden = true;
  const frage = satz("Wirklich? Die Löschung deines Kontos wird angefragt. Das lässt sich nicht zurücknehmen.", "einst-feld-titel");
  const ja = knopf("Ja, Konto löschen anfragen", "danger");
  const nein = knopf("Abbrechen", "ghost");
  bestaetigung.append(frage, ja, nein);
  const kontoMeldung = meldung("einst-feld-satz einst-daten-konto-stand");
  konto.append(
    einstText("Konto löschen", "Du fragst die Löschung an. Sie ist binnen 30 Tagen erledigt, Sicherungen eingeschlossen. Exportiere vorher, was du behalten willst."),
    hakenText,
    anfrage,
    bestaetigung,
    kontoMeldung,
  );

  haken.addEventListener("change", () => {
    anfrage.disabled = !haken.checked;
    if (!haken.checked) bestaetigung.hidden = true;
  });
  anfrage.addEventListener("click", () => {
    if (!haken.checked) return;
    bestaetigung.hidden = false;
    nein.focus();
  });
  nein.addEventListener("click", () => {
    bestaetigung.hidden = true;
    anfrage.focus();
  });
  ja.addEventListener("click", () => {
    ja.disabled = true;
    void k.api
      .kontoLoeschungAnfragen()
      .then(() => {
        bestaetigung.hidden = true;
        anfrage.disabled = true;
        haken.disabled = true;
        kontoMeldung.textContent = "Martin meldet sich; die Löschung ist binnen 30 Tagen erledigt, Sicherungen eingeschlossen.";
      })
      .catch((e: unknown) => {
        ja.disabled = false;
        kontoMeldung.textContent = e instanceof Error && e.message ? e.message : "Das hat nicht geklappt. Bitte später noch einmal.";
      });
  });

  weg.karte.append(kuerzen, korb, konto);

  return {
    elemente: [mit.abschnitt, wo.abschnitt, weg.abschnitt],
    destroy: () => {
      zerstoert = true;
      if (timer !== null) clearTimeout(timer);
      timer = null;
    },
  };
}

/** Die Zeile für die Übersicht: „Letzter Export: 17.10.2026“ oder „noch keiner“. Rein. */
export function letzterExportText(s: ExportStand | null): string {
  if (!s || s.status === "keiner" || !s.angelegtAm) return "noch kein Export";
  const t = new Date(s.angelegtAm);
  if (Number.isNaN(t.getTime())) return "noch kein Export";
  return `Export vom ${t.toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" })}`;
}
